import { useQuery } from '@apollo/client'
import { useEffect } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useCookie } from 'react-use'
import { PublicResolveAccountIdDocument } from '../../generated/graphql'
import { useAuthorization } from '../../hooks/authorization'
import { useRefreshSession } from '../../hooks/refresh-session'
import { RootRoute } from '../../pages'
import {
  ACCOUNT_ID_COOKIE,
  getAccountFromHost,
  getBaseHostname,
  LAST_USED_ACCOUNT_COOKIE,
} from '../../pages/login/util'
import { createRedirectParam } from '../../utils/redirect'

type Props = {
  allowAnonymous?: boolean
}

export const AuthProvider = ({ allowAnonymous = false }: Props) => {
  const { loading: loadingAuthorization, isLoggedIn } = useAuthorization()
  const [, updateAccountIdCookie] = useCookie(ACCOUNT_ID_COOKIE)
  const [, updateLastUsedAccountCookie] = useCookie(LAST_USED_ACCOUNT_COOKIE)

  const accountFromHost = getAccountFromHost()
  const { data, loading: loadingAccount } = useQuery(
    PublicResolveAccountIdDocument,
    {
      skip: !accountFromHost.accountAlias,
      variables: {
        accountAlias: accountFromHost.accountAlias!,
      },
    },
  )
  const isLoading = loadingAuthorization || loadingAccount

  useEffect(() => {
    const accountId =
      data?.publicResolveAccountId.accountId ?? accountFromHost.accountId
    if (accountId) {
      updateAccountIdCookie(accountId.toString())
      updateLastUsedAccountCookie(
        accountFromHost.accountAlias ?? accountId.toString(),
        {
          domain: getBaseHostname(),
          expires: 30,
        },
      )
    }
  }, [
    accountFromHost.accountAlias,
    accountFromHost.accountId,
    data?.publicResolveAccountId.accountId,
    updateAccountIdCookie,
    updateLastUsedAccountCookie,
  ])

  useRefreshSession({ skip: allowAnonymous })

  if (isLoading) {
    return null
  }

  if (!allowAnonymous && !isLoggedIn) {
    return (
      <Navigate
        to={{
          pathname: RootRoute.Logout,
          search: createRedirectParam(),
        }}
      />
    )
  }

  return <Outlet />
}
